import cloud from 'd3-cloud';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';

export type WordCloudTone = 'positive' | 'negative' | 'neutral';

export interface WordCloudTerm {
  readonly term: string;
  readonly weight: number;
  readonly tone: WordCloudTone;
  /** What hovering or focusing the word says about it, e.g. its count and stance split. */
  readonly detail?: string;
}

const TYPE_SCALE_STEPS = [
  'text-caption-sm',
  'text-caption',
  'text-body-sm',
  'text-title-sm',
  'text-title-lg',
] as const;

const TONE_CLASSES: Record<WordCloudTone, string> = {
  positive: 'text-primary-ink',
  negative: 'text-destructive',
  neutral: 'text-muted-foreground',
};

/** Words drawn at each container width: a phone has room for fewer before they turn to dust. */
function wordCap(width: number): number {
  if (width < 480) return 24;
  if (width < 768) return 40;
  return 60;
}

/** Font size bounds in px, by container width. */
function fontRange(width: number): [number, number] {
  return width < 480 ? [12, 34] : [13, 52];
}

function assignQuintileBuckets(terms: readonly WordCloudTerm[]): number[] {
  const n = terms.length;
  if (n === 0) return [];
  if (n === 1) return [4];

  const indexed = terms.map((item, originalIndex) => ({
    originalIndex,
    weight: item.weight,
  }));
  indexed.sort((a, b) => a.weight - b.weight);

  const buckets = new Array<number>(n);
  let i = 0;
  while (i < n) {
    let j = i;
    while (j < n && indexed[j]!.weight === indexed[i]!.weight) {
      j++;
    }
    const midRank = (i + j - 1) / 2;
    const bucket = Math.min(4, Math.max(0, Math.round((midRank / (n - 1)) * 4)));
    for (let k = i; k < j; k++) {
      buckets[indexed[k]!.originalIndex] = bucket;
    }
    i = j;
  }
  return buckets;
}

/** A seeded generator, so the same terms at the same width always land in the same places. */
function seeded(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

interface Placed {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

interface Layout {
  readonly width: number;
  readonly height: number;
  readonly words: readonly Placed[];
}

function useCloudLayout(
  terms: readonly WordCloudTerm[],
  width: number,
  font: string,
): Layout | null {
  const [layout, setLayout] = useState<Layout | null>(null);
  // The terms arrive as a fresh array on every parent render; the layout only
  // re-runs when what it draws changes.
  const latest = useRef(terms);
  latest.current = terms;
  const signature = terms.map((t) => `${t.term}\u0000${t.weight}`).join('\u0001');
  useEffect(() => {
    const terms = latest.current;
    if (terms.length === 0) {
      setLayout(null);
      return;
    }
    if (width === 0) return;
    const shown = [...terms.keys()]
      .sort((a, b) => terms[b]!.weight - terms[a]!.weight)
      .slice(0, wordCap(width));
    const weights = shown.map((i) => terms[i]!.weight);
    const low = Math.sqrt(Math.min(...weights));
    const high = Math.sqrt(Math.max(...weights));
    const [minSize, maxSize] = fontRange(width);
    const sizeOf = (weight: number): number =>
      high === low
        ? (minSize + maxSize) / 2
        : minSize + ((Math.sqrt(weight) - low) / (high - low)) * (maxSize - minSize);
    const height = Math.round(Math.min(Math.max(width * 0.55, 240), 380));

    let cancelled = false;
    const run = cloud<cloud.Word & { size: number; index: number }>()
      .size([width, height])
      .words(
        shown.map((index) => ({
          text: terms[index]!.term,
          size: sizeOf(terms[index]!.weight),
          index,
        })),
      )
      .padding(3)
      .rotate(0)
      .font(font)
      .fontWeight(600)
      .fontSize((word) => word.size)
      .random(seeded(width))
      // One synchronous pass: a background tab throttles d3-cloud's interval timer to
      // seconds, which would leave the wrapped list on screen long after a resize.
      .timeInterval(Infinity)
      .on('end', (placed) => {
        if (cancelled) return;
        setLayout({
          width,
          height,
          // d3-cloud sets x and y on every word it managed to place.
          words: placed.flatMap((word) =>
            word.x === undefined || word.y === undefined
              ? []
              : [{ index: word.index, x: word.x, y: word.y, size: word.size }],
          ),
        });
      });
    run.start();
    return () => {
      cancelled = true;
      run.stop();
    };
  }, [signature, width, font]);
  return layout;
}

/** The container's width, rounded so a scrollbar flicker does not re-run the layout. */
function useWidth(): [RefObject<HTMLDivElement | null>, number, string] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [font, setFont] = useState('sans-serif');
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === 'undefined') return;
    setFont(getComputedStyle(element).fontFamily || 'sans-serif');
    const observer = new ResizeObserver(([entry]) => {
      // A transient zero (a full-page capture, a collapsed ancestor) keeps the last layout.
      if (entry !== undefined && entry.contentRect.width > 0) {
        setWidth(Math.floor(entry.contentRect.width / 16) * 16);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width, font];
}

function FlowList(props: { terms: readonly WordCloudTerm[]; label: string }): ReactNode {
  const buckets = assignQuintileBuckets(props.terms);
  return (
    <div
      role="list"
      aria-label={props.label}
      className="flex flex-wrap items-baseline gap-x-3 gap-y-2"
    >
      {props.terms.map((item, index) => (
        <span
          key={`${item.term}-${index}`}
          role="listitem"
          title={item.detail}
          className={`${TYPE_SCALE_STEPS[buckets[index] ?? 2]!} ${TONE_CLASSES[item.tone]}`}
        >
          {item.term}
        </span>
      ))}
    </div>
  );
}

/**
 * Terms laid out by d3-cloud, sized by weight and coloured by tone; hovering or
 * focusing a word shows its `detail`. Until the container has a width (and in a
 * test DOM, which never lays out) the same terms render as a wrapped list, so
 * the list semantics hold either way.
 */
export function WordCloud(props: { terms: readonly WordCloudTerm[]; label: string }): ReactNode {
  const [ref, width, font] = useWidth();
  const layout = useCloudLayout(props.terms, width, font);

  return (
    <div ref={ref} className="w-full">
      {layout === null ? (
        <FlowList terms={props.terms} label={props.label} />
      ) : (
        <svg
          role="list"
          aria-label={props.label}
          width="100%"
          height={layout.height}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          className="block overflow-visible"
        >
          <g transform={`translate(${layout.width / 2},${layout.height / 2})`}>
            {layout.words.map((word) => {
              const term = props.terms[word.index]!;
              return (
                <Tooltip key={term.term}>
                  <TooltipTrigger asChild>
                    <text
                      role="listitem"
                      tabIndex={0}
                      aria-label={term.detail ?? term.term}
                      textAnchor="middle"
                      transform={`translate(${word.x},${word.y})`}
                      style={{ fontSize: word.size, fontFamily: font, fontWeight: 600 }}
                      className={`${TONE_CLASSES[term.tone]} cursor-default fill-current outline-none hover:opacity-80 focus-visible:underline`}
                    >
                      {term.term}
                    </text>
                  </TooltipTrigger>
                  {term.detail !== undefined && <TooltipContent>{term.detail}</TooltipContent>}
                </Tooltip>
              );
            })}
          </g>
        </svg>
      )}
    </div>
  );
}
